/**
 * Home App Media Registry — AUTO-GENERATED
 *
 * DO NOT EDIT THIS FILE MANUALLY.
 * Edit the "media" field in each app's manifest.json instead,
 * then run: node tools/generate-app-registry.mjs
 *
 * For apps without a manifest media field, add one:
 *   "media": {
 *     "icon": {
 *       "prompt": "A [descriptive] icon, glossy glass-morphism style, ...",
 *       "fallback": "🎮"
 *     }
 *   }
 */

import type { MediaEntry, MediaRegistry } from './types';

const icon = (
  id: string,
  label: string,
  prompt: string,
  fallback: string,
  resolution = 256,
): MediaEntry => ({
  id,
  label,
  type: 'icon',
  resolution,
  prompt,
  fallback,
  negativePrompt: 'text, watermark, blurry, low quality, distorted, deformed',
});

const wallpaper = (
  id: string,
  label: string,
  prompt: string,
  resolution = 1024,
): MediaEntry => ({
  id,
  label,
  type: 'image',
  resolution,
  prompt,
  fallback: '',
  negativePrompt: 'text, watermark, blurry, low quality, distorted, deformed, logo, words, letters',
});

export const HOME_MEDIA: MediaRegistry = {
  // ── Wallpaper ──────────────────────────────────────────────
  'home-wallpaper': wallpaper(
    'home-wallpaper',
    'Home Wallpaper',
    'Abstract dark cosmic nebula background, deep space with subtle purple and blue gradients, smooth bokeh light particles, ethereal atmosphere, ultra high quality, 8k wallpaper, no text',
  ),

  // ── Core apps ──────────────────────────────────────────────
  'admin': icon(
    'admin',
    'Admin',
    'A wrench and shield icon, glossy glass-morphism style, red-orange gradient, dark background, app icon design, 3D render',
    '🛠️',
  ),
  'chat': icon(
    'chat',
    'Chat',
    'A sleek minimal chat bubble icon, glossy glass-morphism style, blue-purple gradient, dark background, app icon design, 3D render',
    '💬',
  ),
  'credits': icon(
    'credits',
    'Credits',
    'A stack of gold coins icon with a subtle glow, glossy glass-morphism style, gold-amber gradient, dark background, app icon design, 3D render',
    '💰',
  ),
  'developer': icon(
    'developer',
    'Developer',
    'A code terminal icon with angle brackets, glossy glass-morphism style, cyan-blue gradient, dark background, app icon design, 3D render',
    '🧪',
  ),
  'openclaw-gateway': icon(
    'openclaw-gateway',
    'OpenClaw Gateway',
    'A futuristic gateway portal with network nodes, glossy glass-morphism style, teal-blue gradient, dark background, app icon design, 3D render',
    '🌐',
  ),
  'settings': icon(
    'settings',
    'Settings',
    'A metallic gear cog icon, glossy glass-morphism style, silver-grey gradient, dark background, app icon design, 3D render',
    '⚙️',
  ),
  'store': icon(
    'store',
    'Store',
    'A modern shopping bag icon with a subtle plus symbol, glossy glass-morphism style, green-teal gradient, dark background, app icon design, 3D render',
    '🛒',
  ),

  // ── Dynamic apps ───────────────────────────────────────────
  'acherun': icon(
    'acherun',
    'Acherun',
    'Crossed swords with a magical aura, glossy glass-morphism style, purple-crimson gradient, dark background, game icon design, 3D render',
    '⚔️',
  ),
  'ai-image': icon(
    'ai-image',
    'AI Image',
    'A paint palette with a magic wand icon, glossy glass-morphism style, rainbow gradient, dark background, app icon design, 3D render',
    '🎨',
  ),
  'auto-chess': icon(
    'auto-chess',
    'Arcane Tactics',
    'A chess board with magical pieces battling autonomously, glossy glass-morphism style, emerald-gold gradient, dark background, game icon design, 3D render',
    '♟️',
  ),
  'board-game': icon(
    'board-game',
    'Board Game',
    'A chess piece knight on a game board, glossy glass-morphism style, wood-brown gradient, dark background, game icon design, 3D render',
    '♟️',
  ),
  'cadival': icon(
    'cadival',
    'Cadival',
    'A medieval fantasy shield with sword and nature vines, glossy glass-morphism style, emerald-gold gradient, dark background, game icon design, 3D render',
    '🛡️',
  ),
  'card-game': icon(
    'card-game',
    'Arcane Summons',
    'Magical playing cards with arcane symbols floating, glossy glass-morphism style, purple-gold gradient, dark background, game icon design, 3D render',
    '🃏',
  ),
  'catalog': icon(
    'catalog',
    'Asset Catalog',
    'A file folder with organized tabs, glossy glass-morphism style, blue-grey gradient, dark background, app icon design, 3D render',
    '🗂️',
  ),
  'dino-run': icon(
    'dino-run',
    'Dino Run',
    'A cute T-Rex dinosaur running, glossy glass-morphism style, green-brown gradient, dark background, game icon design, 3D render',
    '🦖',
  ),
  'docs': icon(
    'docs',
    'Docs',
    'An open book icon with glowing pages, glossy glass-morphism style, indigo-violet gradient, dark background, app icon design, 3D render',
    '📚',
  ),
  'facebook-manager': icon(
    'facebook-manager',
    'Facebook Manager',
    'A social media dashboard with analytics graphs and notification bell, glossy glass-morphism style, blue gradient, dark background, app icon design, 3D render',
    '📘',
  ),
  'flappy-bird': icon(
    'flappy-bird',
    'Flappy Bird',
    'A cute cartoon bird with small wings flying, glossy glass-morphism style, yellow-green gradient, dark background, game icon design, 3D render',
    '🐦',
  ),
  'gambling': icon(
    'gambling',
    'Gambling',
    'A slot machine with lucky sevens, glossy glass-morphism style, gold-red gradient, dark background, game icon design, 3D render',
    '🎰',
  ),
  'last-z': icon(
    'last-z',
    'Last Z',
    'A survivor holding a weapon in a zombie apocalypse, glossy glass-morphism style, dark red-grey gradient, dark background, game icon design, 3D render',
    '🏃‍♂️',
  ),
  'music-studio': icon(
    'music-studio',
    'Music Studio',
    'A sleek music waveform with neon violet and cyan glow, glossy glass-morphism style, deep purple to blue gradient, dark background, premium app icon design, 3D render',
    '🎵',
  ),
  'ninja-fruit': icon(
    'ninja-fruit',
    'Ninja Slash',
    'A katana slicing through a fruit, glossy glass-morphism style, red-silver gradient, dark background, game icon design, 3D render',
    '🗡️',
  ),
  'openclaw': icon(
    'openclaw',
    'OpenClaw',
    'A robotic claw arm with glowing AI circuits, glossy glass-morphism style, cyan-purple gradient, dark background, app icon design, 3D render',
    '🤖',
  ),
  'skull-dash': icon(
    'skull-dash',
    'SkullDash',
    'A stylized skull with speed lines, glossy glass-morphism style, dark purple-black gradient, dark background, game icon design, 3D render',
    '💀',
  ),
  'stack-tower': icon(
    'stack-tower',
    'Stack Tower',
    'Colorful stacked blocks forming a tower, glossy glass-morphism style, multi-color gradient, dark background, game icon design, 3D render',
    '🗼',
  ),
  'sudoku': icon(
    'sudoku',
    'Sudoku',
    'A 9x9 Sudoku grid with glowing numbers, glossy glass-morphism style, blue-indigo gradient, dark background, game icon design, 3D render',
    '🔢',
  ),
  'temple-run': icon(
    'temple-run',
    'Temple Run',
    'A running figure escaping from an ancient temple, glossy glass-morphism style, amber-brown gradient, dark background, game icon design, 3D render',
    '🏃',
  ),
  'tetris-3d': icon(
    'tetris-3d',
    'Tetris 3D',
    'Tetris-shaped 3D blocks floating in space, glossy glass-morphism style, neon gradient, dark background, game icon design, 3D render',
    '🧊',
  ),
  'tiktok-downloader': icon(
    'tiktok-downloader',
    'TikTok Downloader',
    'A downward download arrow over a music note and play button icon, glossy glass-morphism style, teal-pink gradient, dark background, app icon design, 3D render',
    '🎬',
  ),
  'weather': icon(
    'weather',
    'Weather',
    'A sun peeking behind a cloud icon, glossy glass-morphism style, sky-blue and yellow gradient, dark background, app icon design, 3D render',
    '🌤️',
  ),
  'youtube-manager': icon(
    'youtube-manager',
    'YouTube Manager',
    'A YouTube play button icon with channel analytics dashboard, glossy glass-morphism style, red-white gradient, dark background, app icon design, 3D render',
    '📺',
  ),
};

/** Look up a single media entry by ID */
export function getHomeMedia(id: string): MediaEntry | undefined {
  return HOME_MEDIA[id];
}
